import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import type { ChangeEvent } from 'react';
import { Title } from '../../components/Title';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Container,
    Divider,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useMediaQuery,
    Theme,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    Tabs,
    Tab,
    TextField,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import type { QuoteCheckoutInfo } from '../../types/reseller';
import { resellerService } from '../../reseller.service';
import { useMounted } from '../../hooks/use-mounted';
import { Scrollbar } from '../../components/Scrollbar';
import { PropertyList } from '../../components/PropertyList';
import { PropertyListItem } from '../../components/PropertyListItem';
import numeral from 'numeral';
import CreditCard from 'react-credit-cards';
import { formatCreditCardNumber, formatExpirationDate, formatCVC } from '../../utils/credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import creditCards from '../../images/credit-cards.png';
import { IToastState, defaultToastState } from '../../components/ToastState';
import Message from '../Message';
import { settings } from '../../settings';
import { paypalClientId } from '../../AppSettings';
import { cartService } from '../../cart.service';
import type { CheckoutCreateRequest } from '../../cart.service';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const tabs = [
    { label: 'Billing', value: 'billing' },
    { label: 'Shipping', value: 'shipping' },
];

const translateCardType = (issuer: string): string => {
    switch (issuer) {
        case 'visa':
            return 'VISA';
        case 'mastercard':
            return 'MC';
        case 'amex':
            return 'AMEX';
        case 'dinersclub':
            return 'DINE';
        case 'discover':
            return 'DISC';
        default:
            return '';
    }
};

const ANNUAL_SUPPORT_ID = 36;

export const QuoteCheckout = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const align = smDown ? 'vertical' : 'horizontal';

    const isMounted = useMounted();
    const [currentTab, setCurrentTab] = useState<string>('billing');
    const [info, setInfo] = useState<QuoteCheckoutInfo | null>(null);
    const [memo, setMemo] = useState<string>('');
    const [isAnnualSupport, setIsAnnualSupport] = useState<boolean>(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [openBlacklistedDialog, setOpenBlacklistedDialog] = useState(false);
    const [openMonerisDialog, setOpenMonerisDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogFollowLink, setDialogFollowLink] = useState('');
    const [toastState, setToastState] = useState<IToastState>(defaultToastState);
    const { handleSubmit } = useForm<QuoteCheckoutInfo>();

    const inProgressRef = useRef(false);

    // PayPal SDK flow: posts to /cart/paypal/* via cartService.
    const [paypalCheckoutToken, setPaypalCheckoutToken] = useState<string>('');
    const paypalOrderRef = useRef<{ token: string; orderId: string } | null>(null);
    // Carries the server's real create-order failure reason from
    // handlePayPalCreateOrder to the SDK onError callback (which fires after the
    // createOrder promise rejects). Null for SDK-internal errors → generic text.
    const paypalErrorRef = useRef<string | null>(null);

    const [cardNumber, setCardNumber] = useState<string>('');
    const [expiry, setExpiry] = useState<string>('');
    const [cvc, setCvc] = useState<string>('');
    const [focused, setFocused] = useState<string>('');
    const [issuer, setIssuer] = useState<string>('');
    const [inProgress, setInProgress] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>(undefined);

    const loadData = useCallback(async () => {
        try {
            const data = await resellerService.getQuoteCheckoutInfo(orderId);
            if (isMounted()) {
                if (data.ok) {
                    setInfo(data.body);
                    setMemo(data.body.billingFax);
                    if (data.body.items.length === 1 && data.body.items[0].productId === ANNUAL_SUPPORT_ID) {
                        setIsAnnualSupport(true);
                    }
                } else {
                    setLoadError(data.message);
                }
            }
        } catch (err) {
            console.error(err);
        }
    }, [isMounted]);

    useEffect(() => {
        loadData();
    }, []);

    const handleTabsChange = (event: ChangeEvent<{}>, value: string): void => {
        setCurrentTab(value);
    };

    const handleCallback = ({ issuer }, isValid) => {
        if (isValid) {
            setIssuer(issuer);
        } else {
            setIssuer('');
        }
    };

    const handleInputFocus = ({ target }) => {
        setFocused(target.name);
    };

    const handleInputChange = ({ target }) => {
        if (target.name === 'number') {
            target.value = formatCreditCardNumber(target.value);
            setCardNumber(target.value);
        } else if (target.name === 'expiry') {
            target.value = formatExpirationDate(expiry, target.value);
            setExpiry(target.value);
        } else if (target.name === 'cvc') {
            target.value = formatCVC(target.value, cardNumber);
            setCvc(target.value);
        } else if (target.name === 'memo') {
            setMemo(target.value);
        }
    };

    const handleCloseBlacklistedDialog = () => {};
    const handleCancelBlacklistedDialog = () => {
        setOpenBlacklistedDialog(false);
    };

    const handleCloseMonerisDialog = () => {};
    const handleCancelMonerisDialog = () => {
        setOpenMonerisDialog(false);
    };

    // Build the CheckoutCreateRequest for both CC and PayPal flows.
    // Quotes/PORDs pre-exist with admin-trusted amounts; the server's
    // existing-order branch in CartService.CreateCheckout pulls items
    // and totals from ORDERS / ORDER_ITEMS via existingOrderId, so
    // itemsJson/discountCode are ignored here.
    const buildCheckoutRequest = (type: string): CheckoutCreateRequest | null => {
        if (!info) return null;
        return {
            type: type,
            existingOrderId: orderId,
            itemsJson: '[]',
            billingJson: JSON.stringify({
                firstName: info.billingFirstName,
                lastName: info.billingLastName,
                company: info.billingCompany,
                address: info.billingAddress,
                city: info.billingCity,
                state: info.billingState,
                country: info.billingCountry,
                zip: info.billingZip,
                email: info.billingEmail,
                phone: info.billingPhone,
            }),
            comments: isAnnualSupport ? memo : '',
        };
    };

    const onSubmit = async (data: QuoteCheckoutInfo) => {
        if (inProgressRef.current) return;
        if (!info) return;
        inProgressRef.current = true;
        setInProgress(true);

        try {
            const checkoutRequest = buildCheckoutRequest(translateCardType(issuer));
            if (checkoutRequest == null) return;

            // Step 1: create server-side checkout session pointing at the
            // existing PORD/QUOT/INV (server loads its trusted amounts).
            const checkoutResult = await cartService.createCheckout(checkoutRequest);
            if (!checkoutResult.ok || !checkoutResult.body) {
                setDialogTitle('Transaction Failed');
                setDialogMessage(checkoutResult.message || 'Failed to create checkout session');
                setDialogFollowLink('');
                setOpenDialog(true);
                setInProgress(false);
                return;
            }
            const checkoutSession = checkoutResult.body;

            // Step 2: form-POST to OrderProcessBackend.aspx with the token.
            // Memo travels in bill_fax (legacy convention preserved).
            const formRequest = {
                checkout_token: checkoutSession.token,
                credit_card_brand: translateCardType(issuer),
                credit_card_number: cardNumber.replaceAll(' ', ''),
                expire_month: expiry.substr(0, 2),
                expire_year: '20' + expiry.substr(3, 2),
                cvv: cvc,
                bill_fax: memo || '',
            };

            const response = await resellerService.checkout(formRequest);

            if (response == null) {
                setDialogTitle('Transaction Failed');
                setDialogMessage('Server communication error');
                setDialogFollowLink('');
                setOpenDialog(true);
                setInProgress(false);
            } else if (!response.status) {
                if (response.message === 'Blacklisted') {
                    setOpenBlacklistedDialog(true);
                    setInProgress(false);
                } else if (response.message === 'moneris problem') {
                    setOpenMonerisDialog(true);
                    setInProgress(false);
                } else {
                    setDialogTitle('Transaction Failed');
                    setDialogMessage(response.message);
                    setDialogFollowLink('');
                    setOpenDialog(true);
                    setInProgress(false);
                }
            } else {
                setIssuer('');
                setCardNumber('');
                setExpiry('');
                setCvc('');
                setInProgress(false);
                navigate(`/invoices/${response.order}`);
            }
        } finally {
            inProgressRef.current = false;
        }
    };

    const handlePayPalCreateOrder = async (): Promise<string> => {
        if (!info) {
            throw new Error('Quote info not loaded');
        }
        // Guard against the SDK calling createOrder twice for the same click.
        if (paypalOrderRef.current) {
            return paypalOrderRef.current.orderId;
        }

        const checkoutRequest = buildCheckoutRequest('PPAL');
        if (checkoutRequest == null) {
            throw new Error('Quote info not loaded');
        }

        const result = await cartService.createPayPalOrder(checkoutRequest);
        if (!result.ok || !result.body) {
            // Stash the server's reason so onError can show it; the SDK fires
            // onError with its own wrapped error once this promise rejects.
            paypalErrorRef.current = result.message || 'Failed to create PayPal order';
            throw new Error(paypalErrorRef.current);
        }
        paypalOrderRef.current = { token: result.body.checkoutToken, orderId: result.body.payPalOrderId };
        setPaypalCheckoutToken(result.body.checkoutToken);
        return result.body.payPalOrderId;
    };

    const handlePayPalApprove = async (data: { orderID: string }): Promise<void> => {
        setOpenBlacklistedDialog(false);
        setOpenMonerisDialog(false);
        paypalOrderRef.current = null;

        const result = await cartService.capturePayPalOrder(data.orderID, paypalCheckoutToken);
        if (result.ok && result.body?.success && result.body.orderId) {
            navigate(`/invoices/${result.body.orderId}`);
        } else {
            setDialogTitle('Transaction Failed');
            setDialogMessage(result.body?.message || result.message || 'Payment capture failed');
            setDialogFollowLink('');
            setOpenDialog(true);
        }
    };

    const onDialogOk = () => {
        setOpenDialog(false);
        if (dialogFollowLink.length > 0) {
            window.open(dialogFollowLink, '_blank');
        }
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastState({ ...toastState, open: false });
    };

    if (loadError) {
        return <Message title="Invalid Request" message={loadError} />;
    }

    return (
        <>
            <Title title="Checkout" />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 3,
                }}
            >
                <Container maxWidth="lg">
                    <Dialog
                        open={openDialog}
                        onClose={() => setOpenDialog(false)}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">{dialogMessage}</DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => onDialogOk()} autoFocus>
                                OK
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openBlacklistedDialog}
                        onClose={handleCloseBlacklistedDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Payment pre-processing validation failed</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                To process payment with Credit or Debit Card you will be <b>redirected to PayPal</b>{' '}
                                where you can select option to pay with Credit/Debit Card as a guest. After successful
                                payment you will be redirected back here to get an invoice and registration key.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                You can also contact our sales department {settings.phone1} for order processing over
                                the phone.
                            </DialogContentText>
                            <Box sx={{ mt: 2 }}>
                                <PayPalScriptProvider
                                    options={{ clientId: paypalClientId, currency: 'USD', disableFunding: 'paylater' }}
                                >
                                    <PayPalButtons
                                        style={{
                                            layout: 'vertical',
                                            color: 'gold',
                                            shape: 'rect',
                                            label: 'paypal',
                                        }}
                                        createOrder={handlePayPalCreateOrder}
                                        onApprove={handlePayPalApprove}
                                        onError={() => {
                                            paypalOrderRef.current = null;
                                            setOpenBlacklistedDialog(false);
                                            setDialogTitle('PayPal Error');
                                            setDialogMessage(
                                                paypalErrorRef.current ||
                                                    'Payment could not be processed. Please try again.'
                                            );
                                            paypalErrorRef.current = null;
                                            setDialogFollowLink('');
                                            setOpenDialog(true);
                                        }}
                                    />
                                </PayPalScriptProvider>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelBlacklistedDialog} autoFocus>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openMonerisDialog}
                        onClose={handleCloseMonerisDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Payment processing failed</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                We experience payment processing issues with our provider. You can try again to process
                                payment later on, or use PayPal as a credit card payment processor.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                To process payment with Credit or Debit Card you will be <b>redirected to PayPal</b>{' '}
                                where you can select option to pay with Credit/Debit Card as a guest. After successful
                                payment you will be redirected back here to get an invoice and registration key.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                You can also contact our sales department {settings.phone1} for order processing over
                                the phone.
                            </DialogContentText>
                            <Box sx={{ mt: 2 }}>
                                <PayPalScriptProvider
                                    options={{ clientId: paypalClientId, currency: 'USD', disableFunding: 'paylater' }}
                                >
                                    <PayPalButtons
                                        style={{
                                            layout: 'vertical',
                                            color: 'gold',
                                            shape: 'rect',
                                            label: 'paypal',
                                        }}
                                        createOrder={handlePayPalCreateOrder}
                                        onApprove={handlePayPalApprove}
                                        onError={() => {
                                            paypalOrderRef.current = null;
                                            setOpenMonerisDialog(false);
                                            setDialogTitle('PayPal Error');
                                            setDialogMessage(
                                                paypalErrorRef.current ||
                                                    'Payment could not be processed. Please try again.'
                                            );
                                            paypalErrorRef.current = null;
                                            setDialogFollowLink('');
                                            setOpenDialog(true);
                                        }}
                                    />
                                </PayPalScriptProvider>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelMonerisDialog} autoFocus>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={toastState.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity={toastState.status}>
                            {toastState.message}
                        </Alert>
                    </Snackbar>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Order Summary" />
                                <CardContent sx={{ py: 0 }}>
                                    <Scrollbar>
                                        <Box sx={{ minWidth: 700 }}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Product</TableCell>
                                                        <TableCell>License</TableCell>
                                                        <TableCell align="right">Price</TableCell>
                                                        <TableCell align="right">Quantity</TableCell>
                                                        <TableCell align="right">Amount</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {info &&
                                                        info.items.map((product, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{product.productName}</TableCell>
                                                                <TableCell>{product.licenseName}</TableCell>
                                                                <TableCell align="right">
                                                                    {numeral(product.price).format('0,0.00')}
                                                                </TableCell>
                                                                <TableCell align="right">{product.qty}</TableCell>
                                                                <TableCell align="right">
                                                                    {numeral(product.amount).format('0,0.00')}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    {info && info.discount > 0 && (
                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    Discount
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    {numeral(info.discount).format('0,0.00')}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}

                                                    {info && info.tax > 0 && (
                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">Tax</TableCell>
                                                            <TableCell align="right">
                                                                {numeral(info.tax).format('0,0.00')}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}

                                                    {info && (
                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    Order Total
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    {info.currency} $
                                                                    {numeral(info.total).format('0,0.00')}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </Scrollbar>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Tabs
                                    indicatorColor="primary"
                                    onChange={handleTabsChange}
                                    scrollButtons="auto"
                                    textColor="primary"
                                    value={currentTab}
                                    variant="scrollable"
                                >
                                    {tabs.map((tab) => (
                                        <Tab key={tab.value} label={tab.label} value={tab.value} />
                                    ))}
                                </Tabs>
                                <Divider sx={{ mb: 3 }} />
                                {currentTab === 'shipping' && (
                                    <Box sx={{ flexGrow: 1, display: 'flex' }}>
                                        <Card sx={{ flexGrow: 1 }}>
                                            <Box sx={{ px: 3 }}>
                                                <PropertyList>
                                                    <PropertyListItem align={align} label="Name">
                                                        <Typography color="primary" variant="body1">
                                                            {info &&
                                                                `${info.shippingFirstName} ${info.shippingLastName}`}
                                                        </Typography>
                                                    </PropertyListItem>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Company">
                                                            <Typography variant="body1">
                                                                {info && info.shippingCompany}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Address">
                                                            <Typography variant="body1">
                                                                {info && info.shippingAddress}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="City">
                                                            <Typography variant="body1">
                                                                {info && info.shippingCity}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="State / Province">
                                                            <Typography variant="body1">
                                                                {info && info.shippingState}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Zip / Postal code">
                                                            <Typography variant="body1">
                                                                {info && info.shippingZip}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Country">
                                                            <Typography variant="body1">
                                                                {info && info.shippingCountry}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Phone">
                                                            <Typography variant="body1">
                                                                {info && info.shippingPhone}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Email">
                                                            <Typography variant="body1" color="primary">
                                                                {info && info.shippingEmail}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                </PropertyList>
                                            </Box>
                                        </Card>
                                    </Box>
                                )}
                                {currentTab === 'billing' && (
                                    <Box sx={{ flexGrow: 1, display: 'flex' }}>
                                        <Card sx={{ flexGrow: 1 }}>
                                            <Box sx={{ px: 3 }}>
                                                <PropertyList>
                                                    <PropertyListItem align={align} label="Name">
                                                        <Typography color="primary" variant="body1">
                                                            {info && `${info.billingFirstName} ${info.billingLastName}`}
                                                        </Typography>
                                                    </PropertyListItem>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Company">
                                                            <Typography variant="body1">
                                                                {info && info.billingCompany}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Address">
                                                            <Typography variant="body1">
                                                                {info && info.billingAddress}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="City">
                                                            <Typography variant="body1">
                                                                {info && info.billingCity}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="State / Province">
                                                            <Typography variant="body1">
                                                                {info && info.billingState}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Zip / Postal code">
                                                            <Typography variant="body1">
                                                                {info && info.billingZip}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Country">
                                                            <Typography variant="body1">
                                                                {info && info.billingCountry}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Phone">
                                                            <Typography variant="body1">
                                                                {info && info.billingPhone}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                    <Box>
                                                        <PropertyListItem align={align} label="Email">
                                                            <Typography variant="body1" color="primary">
                                                                {info && info.billingEmail}
                                                            </Typography>
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />

                                                    <Box>
                                                        <PropertyListItem
                                                            align={align}
                                                            label={isAnnualSupport ? 'Original Order ID' : 'Memo *'}
                                                        >
                                                            {isAnnualSupport && (
                                                                <Typography color="primary" variant="body1">
                                                                    {info && `${info.comments}`}
                                                                </Typography>
                                                            )}
                                                            {!isAnnualSupport && (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                    }}
                                                                >
                                                                    {info && (
                                                                        <TextField
                                                                            name="memo"
                                                                            size="small"
                                                                            required
                                                                            value={memo}
                                                                            onChange={handleInputChange}
                                                                            onFocus={handleInputFocus}
                                                                            sx={{
                                                                                flexGrow: 1,
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </PropertyListItem>
                                                    </Box>
                                                    <Divider />
                                                </PropertyList>
                                            </Box>
                                        </Card>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Grid item md={6} xs={12}>
                            <Box sx={{ height: '100%' }}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent sx={{ px: 3 }}>
                                        <Box
                                            sx={{
                                                mt: 0,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                    <Typography variant="h6">Pay with &nbsp;</Typography>
                                                    <img alt="credit cards" style={{ width: 340 }} src={creditCards} />
                                                </Box>

                                                <CreditCard
                                                    acceptedCards={[
                                                        'visa',
                                                        'mastercard',
                                                        'amex',
                                                        'dinersclub',
                                                        'discover',
                                                    ]}
                                                    number={cardNumber}
                                                    name=""
                                                    expiry={expiry}
                                                    cvc={cvc}
                                                    focused={focused}
                                                    callback={handleCallback}
                                                />
                                            </Box>
                                            <Box>
                                                <Grid container columnSpacing={3}>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            name="number"
                                                            value={cardNumber}
                                                            required
                                                            label="Card Number"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item md={6} xs={12}>
                                                        <TextField
                                                            name="expiry"
                                                            value={expiry}
                                                            required
                                                            label="Valid Thru (MM/YY)"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item md={6} xs={12}>
                                                        <TextField
                                                            name="cvc"
                                                            value={cvc}
                                                            label="CVC"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <form id="payment" onSubmit={handleSubmit(onSubmit)}>
                                                            <Button
                                                                color="primary"
                                                                variant="contained"
                                                                type="submit"
                                                                fullWidth
                                                                disabled={!issuer || expiry.length !== 5 || inProgress}
                                                                sx={{
                                                                    mt: 3,
                                                                    height: 48,
                                                                    fontSize: '1rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'none',
                                                                }}
                                                            >
                                                                PAY with Card
                                                            </Button>
                                                        </form>

                                                        <Divider sx={{ m: 3 }} variant="middle">
                                                            OR
                                                        </Divider>

                                                        <Box sx={{ mt: 2 }}>
                                                            <PayPalScriptProvider
                                                                options={{
                                                                    clientId: paypalClientId,
                                                                    currency: 'USD',
                                                                    disableFunding: 'paylater',
                                                                }}
                                                            >
                                                                <PayPalButtons
                                                                    style={{
                                                                        layout: 'vertical',
                                                                        color: 'gold',
                                                                        shape: 'rect',
                                                                        label: 'paypal',
                                                                        height: 48,
                                                                    }}
                                                                    createOrder={handlePayPalCreateOrder}
                                                                    onApprove={handlePayPalApprove}
                                                                    onError={() => {
                                                                        paypalOrderRef.current = null;
                                                                        setDialogTitle('PayPal Error');
                                                                        setDialogMessage(
                                                                            paypalErrorRef.current ||
                                                                                'Payment could not be processed. Please try again.'
                                                                        );
                                                                        paypalErrorRef.current = null;
                                                                        setDialogFollowLink('');
                                                                        setOpenDialog(true);
                                                                    }}
                                                                />
                                                            </PayPalScriptProvider>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sx={{}}>
                            <Divider />
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                <Typography variant="subtitle2">
                                    Secure payment services provided by{' '}
                                    <Box component="span" fontWeight="fontWeightBold">
                                        Royal Bank of Canada
                                    </Box>{' '}
                                    and{' '}
                                    <Box component="span" fontWeight="fontWeightBold">
                                        LSoft Technologies
                                    </Box>
                                </Typography>
                            </Box>
                            {info && info.currency !== 'CAD' && (
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                                    <Typography variant="subtitle2" fontSize="80%" sx={{ ml: 1 }}>
                                        You will be charged CAD {numeral(info.total * info.exRate).format('0,0.00')} at
                                        rate {info.exRate}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};
